var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback } from 'react';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { OrganizerSection } from '../OrganizerSection';
import { OrganizerSectionItem } from '../OrganizerSectionItem';
const EmptyPlaceholder = styled(Typography) `
    padding: ${({ theme }) => theme.spacing(0.5, 6)};
`;
const ItemList = (props) => {
    const { items } = props;
    return (_jsx(_Fragment, { children: items.map((item, index) => (_jsx(Draggable, { draggableId: item.id, index: index, children: (provided, snapshot) => (_jsx(OrganizerSectionItem, Object.assign({ isDragging: snapshot.isDragging, showDragHandle: true, dragHandleProps: provided.dragHandleProps || undefined, ref: provided.innerRef }, provided.draggableProps, item))) }, item.id))) }));
};
// Performance optimization - do not rerender when isDraggingOver changes
const ItemListMemoized = memo(ItemList);
export const Organizer = (props) => {
    const { sections, onItemMoved, onItemsUpdated } = props, other = __rest(props, ["sections", "onItemMoved", "onItemsUpdated"]);
    const onDragEnd = useCallback((moveData) => {
        if (!moveData.destination) {
            // placed outside of droppable areas
            return;
        }
        const itemId = moveData.draggableId;
        const source = moveData.source.droppableId;
        const destination = moveData.destination.droppableId;
        const fromIndex = moveData.source.index;
        const toIndex = moveData.destination.index;
        onItemMoved === null || onItemMoved === void 0 ? void 0 : onItemMoved({ itemId, source, destination });
        if (!onItemsUpdated) {
            return;
        }
        const fromSection = sections[source];
        if (!fromSection) {
            console.error(`Unable to find section from which the item was moved: ${source}`);
            return;
        }
        const toSection = sections[destination];
        const movedItem = fromSection.items.find((item) => item.id === itemId);
        if (!toSection) {
            console.error(`Unable to find section to which the item is to be moved: ${destination}`);
            return;
        }
        if (!movedItem) {
            console.error(`Item which was moved is no longer in the source section: ${itemId}`);
            return;
        }
        if (source === destination) {
            // only the item index has updated
            const newItems = [...toSection.items];
            newItems.splice(fromIndex, 1);
            newItems.splice(toIndex, 0, movedItem);
            onItemsUpdated(Object.assign(Object.assign({}, sections), { [source]: Object.assign(Object.assign({}, fromSection), { items: newItems }) }));
            return;
        }
        const newItems = [...toSection.items];
        newItems.splice(toIndex, 0, movedItem);
        const updatedSections = Object.assign(Object.assign({}, sections), { [source]: Object.assign(Object.assign({}, fromSection), { items: fromSection.items.filter((item) => item !== movedItem) }), [destination]: Object.assign(Object.assign({}, toSection), { items: newItems }) });
        onItemsUpdated(updatedSections);
    }, [onItemMoved, onItemsUpdated, sections]);
    const createSection = (id, section) => {
        const { items, emptyPlaceholder } = section, sectionOther = __rest(section, ["items", "emptyPlaceholder"]);
        return (_jsx(Droppable, { droppableId: id, children: (provided, snapshot) => (_jsxs(OrganizerSection, Object.assign({ id: id, dropPlaceholder: provided.placeholder, isDraggingOver: snapshot.isDraggingOver, ref: provided.innerRef }, provided.droppableProps, sectionOther, { children: [_jsx(ItemListMemoized, { items: items }), items.length === 0 && Boolean(emptyPlaceholder) && (_jsx(EmptyPlaceholder, { children: emptyPlaceholder }))] }), id)) }, id));
    };
    return (_jsx(Box, Object.assign({}, other, { children: _jsx(DragDropContext, { onDragEnd: onDragEnd, children: Object.entries(sections).map(([id, section]) => createSection(id, section)) }) })));
};
